<template>
  <Teleport to="body">
    <div v-if="mostrar" class="modal-overlay" @click.self="fecharModal">
      <div :class="['modal-content', { 'modal-fechando': animandoFechamento }]">
        <div class="modal-header">
          <h2>{{ modoEdicao ? 'Editar Campanha' : 'Nova Campanha' }}</h2>
          <button class="btn-close" @click="fecharModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Progress Steps -->
        <div class="steps-indicator">
          <div
            v-for="step in 3"
            :key="step"
            class="step"
            :class="{ active: passoAtual >= step, current: passoAtual === step }"
          >
            <span class="step-number">{{ step }}</span>
            <span class="step-label">{{ getStepLabel(step) }}</span>
          </div>
        </div>

        <div class="modal-body">
          <!-- Passo 1: Selecionar Template e Canal -->
          <div v-if="passoAtual === 1" class="step-content">
            <div class="form-group">
              <label>Nome da Campanha *</label>
              <input
                v-model="form.nome"
                type="text"
                class="form-input"
                placeholder="Ex: Promoção de Janeiro 2026"
                maxlength="150"
              />
            </div>

            <div class="form-group">
              <label>Canal de Envio *</label>
              <div class="canal-options">
                <label class="canal-option" :class="{ selected: form.canal === 'email' }">
                  <input v-model="form.canal" type="radio" value="email" />
                  <i class="fas fa-envelope"></i>
                  <span>Email</span>
                </label>
                <label class="canal-option" :class="{ selected: form.canal === 'whatsapp' }">
                  <input v-model="form.canal" type="radio" value="whatsapp" />
                  <i class="fab fa-whatsapp"></i>
                  <span>WhatsApp</span>
                </label>
              </div>
            </div>

            <div class="form-group">
              <label>Template de Mensagem *</label>
              <select v-model="form.template_id" class="form-input">
                <option :value="null">Selecione um template...</option>
                <option
                  v-for="template in templatesDisponiveis"
                  :key="template.id"
                  :value="template.id"
                >
                  {{ template.nome }}
                  <span v-if="template.usar_ia">(IA)</span>
                </option>
              </select>
              <p v-if="!templatesDisponiveis.length" class="help-text">
                Nenhum template disponível para este canal. Crie um template primeiro.
              </p>
            </div>

            <!-- Preview do template selecionado -->
            <div v-if="templateSelecionado" class="template-preview">
              <h4>Preview do Template</h4>
              <div class="preview-box">
                <p v-if="templateSelecionado.assunto" class="preview-assunto">
                  <strong>Assunto:</strong> {{ templateSelecionado.assunto }}
                </p>
                <p class="preview-corpo">{{ truncateText(templateSelecionado.corpo, 200) }}</p>
                <span v-if="templateSelecionado.usar_ia" class="ia-badge">
                  <i class="fas fa-robot"></i> Conteúdo gerado por IA
                </span>
              </div>
            </div>
          </div>

          <!-- Passo 2: Filtrar Destinatários -->
          <div v-if="passoAtual === 2" class="step-content">
            <h3 class="step-title">Filtrar Destinatários</h3>
            <p class="step-description">
              Selecione os filtros para definir quem receberá esta campanha.
              Apenas pacientes que aceitaram receber {{ form.canal === 'email' ? 'emails' : 'WhatsApp' }} serão incluídos.
            </p>

            <div class="filtros-sections">
              <!-- UF -->
              <div class="form-group">
                <div class="filtro-header">
                  <label>Estados (UF)</label>
                  <button
                    v-if="form.filtros.uf.length > 0"
                    type="button"
                    class="btn-limpar-filtro"
                    @click="limparFiltroUF"
                  >
                    Limpar
                  </button>
                  <span v-else class="filtro-hint">Todos selecionados</span>
                </div>
                <div class="chips-grid chips-uf">
                  <button
                    v-for="uf in listaUFs"
                    :key="uf.sigla"
                    type="button"
                    class="chip"
                    :class="{ selected: form.filtros.uf.includes(uf.sigla) }"
                    :title="uf.nome"
                    @click="toggleFiltroUF(uf.sigla)"
                  >
                    {{ uf.sigla }}
                  </button>
                </div>
              </div>

              <!-- Sistema -->
              <div class="form-group">
                <div class="filtro-header">
                  <label>Sistema Inscrito</label>
                  <button
                    v-if="form.filtros.sistema_inscrito.length > 0"
                    type="button"
                    class="btn-limpar-filtro"
                    @click="limparFiltroSistema"
                  >
                    Limpar
                  </button>
                  <span v-else class="filtro-hint">Todos selecionados</span>
                </div>
                <div class="chips-grid chips-sistema">
                  <button
                    v-for="sistema in sistemas"
                    :key="sistema"
                    type="button"
                    class="chip chip-sistema"
                    :class="{ selected: form.filtros.sistema_inscrito.includes(sistema) }"
                    @click="toggleFiltroSistema(sistema)"
                  >
                    {{ sistema }}
                  </button>
                </div>
              </div>

              <!-- Sexo + Idade lado a lado -->
              <div class="filtros-row">
                <div class="form-group">
                  <div class="filtro-header">
                    <label>Sexo</label>
                    <button
                      v-if="form.filtros.sexo.length > 0"
                      type="button"
                      class="btn-limpar-filtro"
                      @click="limparFiltroSexo"
                    >
                      Limpar
                    </button>
                  </div>
                  <div class="chips-grid">
                    <button
                      type="button"
                      class="chip chip-sexo"
                      :class="{ selected: form.filtros.sexo.includes('F') }"
                      @click="toggleFiltroSexo('F')"
                    >
                      Feminino
                    </button>
                    <button
                      type="button"
                      class="chip chip-sexo"
                      :class="{ selected: form.filtros.sexo.includes('M') }"
                      @click="toggleFiltroSexo('M')"
                    >
                      Masculino
                    </button>
                    <button
                      type="button"
                      class="chip chip-sexo"
                      :class="{ selected: form.filtros.sexo.includes('O') }"
                      @click="toggleFiltroSexo('O')"
                    >
                      Outro
                    </button>
                  </div>
                </div>

                <div class="form-group">
                  <label>Faixa Etária</label>
                  <div class="idade-range">
                    <input
                      v-model.number="form.filtros.idade_min"
                      type="number"
                      class="form-input-small"
                      placeholder="Min"
                      min="0"
                    />
                    <span>até</span>
                    <input
                      v-model.number="form.filtros.idade_max"
                      type="number"
                      class="form-input-small"
                      placeholder="Max"
                      min="0"
                    />
                    <span>anos</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Contador de destinatários -->
            <div class="destinatarios-count" :class="{ loading: contandoDestinatarios }">
              <div class="count-box">
                <span v-if="contandoDestinatarios" class="count-loading">
                  <i class="fas fa-spinner fa-spin"></i> Contando...
                </span>
                <template v-else>
                  <span class="count-number">{{ totalDestinatarios }}</span>
                  <span class="count-label">destinatários encontrados</span>
                </template>
              </div>
              <button type="button" class="btn-refresh" :disabled="contandoDestinatarios" @click="atualizarContagem">
                <i class="fas fa-sync-alt" :class="{ 'fa-spin': contandoDestinatarios }"></i>
                {{ contandoDestinatarios ? 'Contando...' : 'Atualizar contagem' }}
              </button>
            </div>
          </div>

          <!-- Passo 3: Revisar e Enviar -->
          <div v-if="passoAtual === 3" class="step-content">
            <h3 class="step-title">Revisar Campanha</h3>

            <div class="review-section">
              <div class="review-item">
                <span class="review-label">Nome:</span>
                <span class="review-value">{{ form.nome }}</span>
              </div>
              <div class="review-item">
                <span class="review-label">Canal:</span>
                <span class="review-value">
                  <span class="canal-badge" :class="form.canal">
                    <i :class="form.canal === 'email' ? 'fas fa-envelope' : 'fab fa-whatsapp'"></i>
                    {{ form.canal === 'email' ? 'Email' : 'WhatsApp' }}
                  </span>
                </span>
              </div>
              <div class="review-item">
                <span class="review-label">Template:</span>
                <span class="review-value">{{ templateSelecionado?.nome || '-' }}</span>
              </div>
              <div class="review-item">
                <span class="review-label">Destinatários:</span>
                <span class="review-value highlight">{{ totalDestinatarios }} pacientes</span>
              </div>
            </div>

            <div class="review-filters">
              <h4>Filtros aplicados:</h4>
              <ul>
                <li v-if="form.filtros.uf.length">
                  <strong>UFs:</strong> {{ form.filtros.uf.join(', ') }}
                </li>
                <li v-if="form.filtros.sistema_inscrito.length">
                  <strong>Sistemas:</strong> {{ form.filtros.sistema_inscrito.join(', ') }}
                </li>
                <li v-if="form.filtros.sexo.length">
                  <strong>Sexo:</strong> {{ form.filtros.sexo.join(', ') }}
                </li>
                <li v-if="form.filtros.idade_min || form.filtros.idade_max">
                  <strong>Idade:</strong>
                  {{ form.filtros.idade_min || 0 }} - {{ form.filtros.idade_max || '∞' }} anos
                </li>
                <li v-if="!temFiltros">
                  <em>Nenhum filtro aplicado - todos os pacientes elegíveis</em>
                </li>
              </ul>
            </div>

            <!-- Agendamento -->
            <div class="agendamento-box">
              <div class="agendamento-toggle">
                <label class="toggle-label">
                  <input v-model="agendarEnvio" type="checkbox" class="toggle-input" />
                  <span class="toggle-slider"></span>
                  <span class="toggle-text">
                    <i class="fas fa-clock"></i>
                    Agendar envio para depois
                  </span>
                </label>
              </div>
              <div v-if="agendarEnvio" class="agendar-datetime">
                <label>Data e hora do envio</label>
                <input
                  v-model="form.agendado_para"
                  type="datetime-local"
                  class="form-input"
                  :min="minDateTime"
                />
                <span class="agendar-hint">A campanha será enviada automaticamente na data e hora selecionadas.</span>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button v-if="passoAtual > 1" type="button" class="btn-voltar" @click="voltarPasso">
            <i class="fas fa-arrow-left"></i> Voltar
          </button>
          <div class="footer-right">
            <button type="button" class="btn-cancelar" @click="fecharModal">
              Cancelar
            </button>
            <button
              v-if="passoAtual < 3"
              type="button"
              class="btn-proximo"
              :disabled="!podeAvancar"
              @click="avancarPasso"
            >
              Próximo <i class="fas fa-arrow-right"></i>
            </button>
            <button
              v-else
              type="button"
              class="btn-salvar"
              :disabled="loading || totalDestinatarios === 0"
              @click="salvar"
            >
              <span v-if="loading" class="spinner-btn"></span>
              {{ agendarEnvio ? 'Agendar Campanha' : 'Salvar Campanha' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import Swal from 'sweetalert2'
import dayjs from 'dayjs'
import { listaUFs } from '@/utils/ufs'
import { useSistemasInscritos } from '@/composables/useSistemasInscritos'

// Carregar sistemas inscritos da API
const { sistemas: sistemasInscritos, carregar: carregarSistemas } = useSistemasInscritos()
carregarSistemas()

const props = defineProps({
  mostrar: { type: Boolean, default: false },
  modoEdicao: { type: Boolean, default: false },
  campanha: { type: Object, default: null },
  templates: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['fechar', 'salvar', 'contar-destinatarios'])

const animandoFechamento = ref(false)
const passoAtual = ref(1)
const totalDestinatarios = ref(0)
const contandoDestinatarios = ref(false)
const agendarEnvio = ref(false)

const sistemas = sistemasInscritos // Reactive ref da API

const formPadrao = () => ({
  nome: '',
  template_id: null,
  canal: 'email',
  filtros: {
    uf: [],
    sistema_inscrito: [],
    sexo: [],
    idade_min: null,
    idade_max: null,
  },
  agendado_para: null,
})

const form = ref(formPadrao())

// Computed
const templatesDisponiveis = computed(() => {
  return props.templates.filter(t => {
    if (!t.ativo) return false
    if (t.canal === 'ambos') return true
    return t.canal === form.value.canal
  })
})

const templateSelecionado = computed(() => {
  if (!form.value.template_id) return null
  return props.templates.find(t => t.id === form.value.template_id)
})

const temFiltros = computed(() => {
  const f = form.value.filtros
  return f.uf.length > 0 ||
    f.sistema_inscrito.length > 0 ||
    f.sexo.length > 0 ||
    f.idade_min ||
    f.idade_max
})

const podeAvancar = computed(() => {
  if (passoAtual.value === 1) {
    return form.value.nome?.trim() && form.value.canal && form.value.template_id
  }
  if (passoAtual.value === 2) {
    return true // Pode avançar mesmo sem filtros
  }
  return true
})

const minDateTime = computed(() => {
  return dayjs().add(5, 'minute').format('YYYY-MM-DDTHH:mm')
})

// Watch
watch(
  () => props.mostrar,
  (aberto) => {
    if (aberto) {
      if (props.modoEdicao && props.campanha) {
        form.value = {
          ...formPadrao(),
          ...props.campanha,
          filtros: { ...formPadrao().filtros, ...props.campanha.filtros },
        }
        agendarEnvio.value = !!props.campanha.agendado_para
        totalDestinatarios.value = props.campanha.total_destinatarios || 0
      } else {
        form.value = formPadrao()
        agendarEnvio.value = false
        totalDestinatarios.value = 0
      }
      passoAtual.value = 1
    }
  },
  { immediate: true }
)

watch(
  () => form.value.canal,
  () => {
    // Reset template se mudou o canal e o template não é compatível
    if (templateSelecionado.value) {
      const t = templateSelecionado.value
      if (t.canal !== 'ambos' && t.canal !== form.value.canal) {
        form.value.template_id = null
      }
    }
  }
)

watch(agendarEnvio, (agendar) => {
  if (!agendar) {
    form.value.agendado_para = null
  }
})

// Methods
function getStepLabel(step) {
  const labels = {
    1: 'Template',
    2: 'Destinatários',
    3: 'Revisar',
  }
  return labels[step]
}

function truncateText(text, maxLength) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

function avancarPasso() {
  if (passoAtual.value < 3 && podeAvancar.value) {
    passoAtual.value++
    if (passoAtual.value === 2) {
      atualizarContagem()
    }
  }
}

function voltarPasso() {
  if (passoAtual.value > 1) {
    passoAtual.value--
  }
}

function toggleFiltroUF(sigla) {
  const idx = form.value.filtros.uf.indexOf(sigla)
  if (idx >= 0) {
    form.value.filtros.uf.splice(idx, 1)
  } else {
    form.value.filtros.uf.push(sigla)
  }
}

function limparFiltroUF() {
  form.value.filtros.uf = []
}

function toggleFiltroSistema(sistema) {
  const idx = form.value.filtros.sistema_inscrito.indexOf(sistema)
  if (idx >= 0) {
    form.value.filtros.sistema_inscrito.splice(idx, 1)
  } else {
    form.value.filtros.sistema_inscrito.push(sistema)
  }
}

function limparFiltroSistema() {
  form.value.filtros.sistema_inscrito = []
}

function toggleFiltroSexo(sexo) {
  const idx = form.value.filtros.sexo.indexOf(sexo)
  if (idx >= 0) {
    form.value.filtros.sexo.splice(idx, 1)
  } else {
    form.value.filtros.sexo.push(sexo)
  }
}

function limparFiltroSexo() {
  form.value.filtros.sexo = []
}

async function atualizarContagem() {
  contandoDestinatarios.value = true
  emit('contar-destinatarios', form.value, (count) => {
    totalDestinatarios.value = count
    contandoDestinatarios.value = false
  })
}

function validarForm() {
  if (!form.value.nome?.trim()) {
    Swal.fire('Atenção', 'Nome da campanha é obrigatório.', 'warning')
    return false
  }

  if (!form.value.template_id) {
    Swal.fire('Atenção', 'Selecione um template.', 'warning')
    return false
  }

  if (totalDestinatarios.value === 0) {
    Swal.fire('Atenção', 'Nenhum destinatário encontrado com os filtros selecionados.', 'warning')
    return false
  }

  if (agendarEnvio.value && !form.value.agendado_para) {
    Swal.fire('Atenção', 'Selecione a data e hora do agendamento.', 'warning')
    return false
  }

  return true
}

function salvar() {
  if (!validarForm()) return
  emit('salvar', { ...form.value, total_destinatarios: totalDestinatarios.value })
}

function fecharModal() {
  animandoFechamento.value = true
  setTimeout(() => {
    animandoFechamento.value = false
    emit('fechar')
  }, 250)
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 750px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: fadeSlideIn 0.3s ease-out;
}

.modal-content.modal-fechando {
  animation: fadeSlideOut 0.25s ease-in forwards;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  color: #333;
}

.btn-close {
  width: 36px;
  height: 36px;
  border: none;
  background: #f5f5f5;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  color: #666;
}

/* Steps indicator */
.steps-indicator {
  display: flex;
  justify-content: center;
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
}

.step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 20px;
  position: relative;
}

.step:not(:last-child)::after {
  content: '';
  position: absolute;
  right: -20px;
  width: 40px;
  height: 2px;
  background: #ddd;
}

.step.active:not(:last-child)::after {
  background: #4caf50;
}

.step-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #ddd;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
}

.step.active .step-number {
  background: #4caf50;
  color: white;
}

.step.current .step-number {
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.3);
}

.step-label {
  font-size: 13px;
  color: #888;
}

.step.active .step-label {
  color: #333;
  font-weight: 500;
}

.modal-body {
  padding: 25px;
}

.step-content {
  min-height: 300px;
}

.step-title {
  font-size: 18px;
  margin: 0 0 10px 0;
  color: #333;
}

.step-description {
  color: #666;
  margin-bottom: 20px;
}

/* Form styles */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
  font-size: 14px;
}

.form-input {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #4caf50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

.form-input-small {
  width: 80px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
}

.help-text {
  font-size: 12px;
  color: #888;
  margin-top: 6px;
}

/* Canal options */
.canal-options {
  display: flex;
  gap: 12px;
}

.canal-option {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.canal-option input {
  display: none;
}

.canal-option i {
  font-size: 28px;
  color: #666;
}

.canal-option span {
  font-size: 14px;
  font-weight: 600;
  color: #666;
}

.canal-option.selected {
  border-color: #4caf50;
  background: #f1f8e9;
}

.canal-option.selected i,
.canal-option.selected span {
  color: #388e3c;
}

/* Template preview */
.template-preview {
  margin-top: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.template-preview h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #666;
}

.preview-box {
  background: white;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 15px;
}

.preview-assunto {
  margin: 0 0 10px 0;
  font-size: 13px;
}

.preview-corpo {
  margin: 0;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

.ia-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  padding: 4px 8px;
  background: #f3e5f5;
  color: #7b1fa2;
  border-radius: 4px;
  font-size: 11px;
}

/* Filtros */
.filtros-sections {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filtro-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.filtro-header label {
  margin-bottom: 0 !important;
}

.filtro-hint {
  font-size: 11px;
  color: #999;
  font-style: italic;
}

.btn-limpar-filtro {
  background: none;
  border: none;
  color: #e53935;
  font-size: 12px;
  cursor: pointer;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
}

.btn-limpar-filtro:hover {
  background: #fce4ec;
}

.chips-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chips-uf {
  max-height: 110px;
  overflow-y: auto;
  padding: 4px 2px;
}

.chip {
  padding: 5px 12px;
  border: 1px solid #ddd;
  border-radius: 20px;
  background: #f5f5f5;
  color: #555;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.chip:hover {
  border-color: #4caf50;
  background: #f1f8e9;
}

.chip.selected {
  background: #4caf50;
  color: white;
  border-color: #4caf50;
}

.chip-sistema {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.filtros-row {
  display: flex;
  gap: 20px;
}

.filtros-row .form-group {
  flex: 1;
}

.idade-range {
  display: flex;
  align-items: center;
  gap: 10px;
}

.idade-range span {
  color: #666;
  font-size: 14px;
}

/* Destinatarios count */
.destinatarios-count {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  padding: 15px;
  background: #e8f5e9;
  border-radius: 8px;
}

.count-box {
  display: flex;
  flex-direction: column;
}

.count-number {
  font-size: 28px;
  font-weight: 700;
  color: #388e3c;
}

.count-label {
  font-size: 13px;
  color: #666;
}

.btn-refresh {
  padding: 8px 15px;
  background: white;
  border: 1px solid #388e3c;
  border-radius: 6px;
  color: #388e3c;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-refresh:hover:not(:disabled) {
  background: #f1f8e9;
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.count-loading {
  font-size: 16px;
  color: #666;
  font-weight: 500;
}

.count-loading i {
  color: #4caf50;
  margin-right: 6px;
}

/* Review section */
.review-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.review-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}

.review-item:last-child {
  border-bottom: none;
}

.review-label {
  color: #666;
}

.review-value {
  font-weight: 600;
  color: #333;
}

.review-value.highlight {
  color: #388e3c;
  font-size: 18px;
}

.canal-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
}

.canal-badge.email {
  background: #e3f2fd;
  color: #1976d2;
}

.canal-badge.whatsapp {
  background: #e8f5e9;
  color: #388e3c;
}

.review-filters {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
}

.review-filters h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #666;
}

.review-filters ul {
  margin: 0;
  padding-left: 20px;
}

.review-filters li {
  margin-bottom: 5px;
  font-size: 13px;
  color: #555;
}

/* Agendamento */
.agendamento-box {
  margin-top: 20px;
  padding: 18px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fafafa;
}

.agendamento-toggle {
  display: flex;
  align-items: center;
}

.form-group label.toggle-label,
.agendamento-toggle .toggle-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  margin-bottom: 0;
  font-weight: 500;
}

.toggle-input {
  display: none;
}

.toggle-slider {
  width: 44px;
  min-width: 44px;
  height: 24px;
  background: #ccc;
  border-radius: 12px;
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
}

.toggle-slider::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  top: 2px;
  left: 2px;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-input:checked + .toggle-slider {
  background: #4caf50;
}

.toggle-input:checked + .toggle-slider::after {
  transform: translateX(20px);
}

.toggle-text {
  font-weight: 500;
  color: #555;
  display: flex;
  align-items: center;
  gap: 8px;
}

.toggle-text i {
  color: #1976d2;
}

.agendar-datetime {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.agendar-datetime label {
  display: block;
  font-weight: 600;
  font-size: 13px;
  color: #333;
  margin-bottom: 6px;
}

.agendar-datetime .form-input {
  max-width: 280px;
}

.agendar-hint {
  display: block;
  font-size: 11px;
  color: #888;
  margin-top: 6px;
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-top: 1px solid #eee;
}

.footer-right {
  display: flex;
  gap: 12px;
}

.btn-voltar,
.btn-cancelar,
.btn-proximo,
.btn-salvar {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;
}

.btn-voltar {
  background: #f5f5f5;
  color: #666;
}

.btn-cancelar {
  background: #f5f5f5;
  color: #666;
}

.btn-proximo {
  background: #2196f3;
  color: white;
}

.btn-proximo:disabled {
  background: #bdbdbd;
  cursor: not-allowed;
}

.btn-salvar {
  background: #4caf50;
  color: white;
}

.btn-salvar:disabled {
  background: #bdbdbd;
  cursor: not-allowed;
}

.spinner-btn {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeSlideOut {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(20px);
  }
}

/* Dark mode */
.dark-mode .modal-content {
  background: #1e1e1e;
}

.dark-mode .modal-header,
.dark-mode .steps-indicator {
  border-color: #333;
  background: #252525;
}

.dark-mode .modal-header h2,
.dark-mode .step-title {
  color: #f1f1f1;
}

.dark-mode .form-input {
  background: #2a2a2a;
  border-color: #444;
  color: #f1f1f1;
}

.dark-mode .template-preview,
.dark-mode .review-section {
  background: #252525;
}

.dark-mode .preview-box,
.dark-mode .review-filters {
  background: #2a2a2a;
  border-color: #444;
}

.dark-mode .chip {
  background: #2a2a2a;
  border-color: #444;
  color: #ccc;
}

.dark-mode .chip:hover {
  background: #1b4332;
  border-color: #4caf50;
}

.dark-mode .chip.selected {
  background: #388e3c;
  border-color: #4caf50;
  color: white;
}

.dark-mode .filtro-hint {
  color: #666;
}

.dark-mode .btn-limpar-filtro {
  color: #ef5350;
}

.dark-mode .btn-limpar-filtro:hover {
  background: #3e2723;
}

.dark-mode .destinatarios-count {
  background: #1b4332;
}

.dark-mode .count-number {
  color: #81c784;
}

.dark-mode .btn-refresh {
  background: #252525;
  border-color: #4caf50;
  color: #81c784;
}

.dark-mode .form-input-small {
  background: #2a2a2a;
  border-color: #444;
  color: #f1f1f1;
}

.dark-mode .agendamento-box {
  background: #252525;
  border-color: #3a3a3a;
}

.dark-mode .agendar-datetime {
  border-top-color: #3a3a3a;
}

.dark-mode .agendar-datetime label {
  color: #e0e0e0;
}

.dark-mode .agendar-hint {
  color: #666;
}

.dark-mode .toggle-text {
  color: #ccc;
}

.dark-mode .toggle-text i {
  color: #64b5f6;
}

@media (max-width: 600px) {
  .modal-content {
    max-height: 100vh;
    border-radius: 0;
  }

  .steps-indicator {
    padding: 15px 10px;
  }

  .step {
    padding: 0 10px;
  }

  .step:not(:last-child)::after {
    right: -10px;
    width: 20px;
  }

  .step-label {
    display: none;
  }

  .canal-options {
    flex-direction: column;
  }

  .filtros-row {
    flex-direction: column;
  }

  .modal-footer {
    flex-direction: column;
    gap: 10px;
  }

  .footer-right {
    width: 100%;
    flex-direction: column-reverse;
  }

  .btn-voltar,
  .btn-cancelar,
  .btn-proximo,
  .btn-salvar {
    width: 100%;
    justify-content: center;
  }
}
</style>
